import {
    CheckOutlined,
    ClockCircleOutlined,
    CloseOutlined,
    CopyOutlined,
    DeploymentUnitOutlined,
    EditOutlined,
    GlobalOutlined,
    PlayCircleOutlined,
    PoweroffOutlined,
    SettingOutlined,
    ShareAltOutlined,
    StopOutlined,
    SyncOutlined,
    ThunderboltOutlined,
} from "@ant-design/icons";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";

interface IconProps {
    className?: string;
}

// All icons use fontSize: "1em" to inherit size from parent container

// Start - Play icon (outlined)
export const StartIcon: FC<IconProps> = ({ className }) => (
    <PlayCircleOutlined style={{ fontSize: "1em" }} className={className} />
);

// End - Power off icon
export const EndIcon: FC<IconProps> = ({ className }) => (
    <PoweroffOutlined style={{ fontSize: "1em" }} className={className} />
);

// Wait - Clock icon
export const WaitIcon: FC<IconProps> = ({ className }) => (
    <ClockCircleOutlined style={{ fontSize: "1em" }} className={className} />
);

// Repeater - Sync icon
export const RepeaterIcon: FC<IconProps> = ({ className }) => (
    <SyncOutlined style={{ fontSize: "1em" }} className={className} />
);

// Breaker - Stop icon
export const BreakerIcon: FC<IconProps> = ({ className }) => (
    <StopOutlined style={{ fontSize: "1em" }} className={className} />
);

// Modify - Edit icon
export const ModifyIcon: FC<IconProps> = ({ className }) => (
    <EditOutlined style={{ fontSize: "1em" }} className={className} />
);

// Set - Settings icon
export const SetIcon: FC<IconProps> = ({ className }) => (
    <SettingOutlined style={{ fontSize: "1em" }} className={className} />
);

// HTTP - Globe icon
export const HttpIcon: FC<IconProps> = ({ className }) => (
    <GlobalOutlined style={{ fontSize: "1em" }} className={className} />
);

// Webhook - Deployment unit icon
export const WebhookIcon: FC<IconProps> = ({ className }) => (
    <DeploymentUnitOutlined style={{ fontSize: "1em" }} className={className} />
);

// SubScenario - Share icon
export const SubScenarioIcon: FC<IconProps> = ({ className }) => (
    <ShareAltOutlined style={{ fontSize: "1em" }} className={className} />
);

// Mirror - Copy icon
export const MirrorIcon: FC<IconProps> = ({ className }) => (
    <CopyOutlined style={{ fontSize: "1em" }} className={className} />
);

// Start variant badges - circle, grey border, white bg, bigger size
const badgeBaseClass =
    "absolute -bottom-2 -right-2 w-8 h-8 bg-white rounded-[100%] flex items-center justify-center border border-gray-400";

export const PostBadge: FC<{ className?: string }> = ({ className }) => (
    <div className={`${badgeBaseClass} ${className}`}>
        {/* eslint-disable-next-line i18next/no-literal-string */}
        <span className="text-[10px] font-bold text-black">Post</span>
    </div>
);

export const PingBadge: FC<{ className?: string }> = ({ className }) => (
    <div className={`${badgeBaseClass} ${className}`}>
        {/* eslint-disable-next-line i18next/no-literal-string */}
        <span className="text-[10px] font-bold text-black">Ping</span>
    </div>
);

export const PostVerifyBadge: FC<{ className?: string }> = ({ className }) => {
    const { t } = usePaldockTranslation("components.connection-creator");
    return (
        <div className={`${badgeBaseClass} ${className}`}>
            <span className="text-[8px] font-bold text-black leading-tight text-center">
                {t("enums.flow_types.badge_verify")}
                <br />
                {t("enums.flow_types.badge_post")}
            </span>
        </div>
    );
};

export const WebhookBadge: FC<{ className?: string }> = ({ className }) => (
    <div className={`${badgeBaseClass} ${className}`}>
        <DeploymentUnitOutlined style={{ fontSize: 16, color: "#9CA3AF" }} />
    </div>
);

export const InstantBadge: FC<{ className?: string }> = ({ className }) => (
    <div className={`${badgeBaseClass} ${className}`}>
        <ThunderboltOutlined style={{ fontSize: 16, color: "#9CA3AF" }} />
    </div>
);

// End outcome badges
export const SuccessOutcomeBadge: FC<{ className?: string }> = ({ className }) => (
    <div className={`${badgeBaseClass} ${className || ""}`}>
        <CheckOutlined style={{ fontSize: 14, color: "#9CA3AF" }} />
    </div>
);

export const FailedOutcomeBadge: FC<{ className?: string }> = ({ className }) => (
    <div className={`${badgeBaseClass} ${className || ""}`}>
        <CloseOutlined style={{ fontSize: 14, color: "#9CA3AF" }} />
    </div>
);

// Sort order badge - displayed on top-right of node
export const SortOrderBadge: FC<{ order: number; className?: string }> = ({ order, className }) => (
    <div
        className={`absolute -top-1 -right-1 w-5 h-5 bg-gray-300 rounded-[100%] flex items-center justify-center text-gray-500 text-xs font-medium ${className || ""}`}
    >
        {order}
    </div>
);

// Mirror badge - shows the sort order of the mirrored element (same style as Start badges)
export const MirrorNumberBadge: FC<{ number: number; className?: string }> = ({ number, className }) => (
    <div className={`${badgeBaseClass} ${className || ""}`}>
        <span className="text-sm font-bold text-gray-600">{number}</span>
    </div>
);
